import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { forwardRef, useCallback, useEffect, useRef } from 'react';

// Tell ECharts which components to use
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
]);

const BaseChart = forwardRef(
  (
    {
      option, // ECharts configuration object
      onEvents = {}, // Event handlers for ECharts events (click, mouseover, etc.)
      eChartsRenderer = 'canvas', // Can be 'canvas' or 'svg'
      notMerge = false, // If true, ECharts will recreate and replace the option object on each update with the new one
      autoResize = true, // If true, chart will automatically resize when container size changes
      lazyUpdate = false, // If true, chart will not update immediately on option change it will defer rendering for a later point (improves performance)
      customStyles = {},
      height = '100%',
      width = '100%',
      controlsLeft = null, // optional overlay controls (top-left)
      controlsRight = null, // optional overlay controls (top-right)
    },
    ref,
  ) => {
    const containerRef = useRef(null);

    // Intercept wheel events on the chart container (capture phase) so we can
    // redirect them to the tooltip content when it is visible and scrollable.
    // This runs before ECharts' own handler, giving us a chance to prevent zoom.
    const wheelHandler = useCallback((e) => {
      // Find the visible tooltip portal created by this chart
      const portal = document.querySelector('.echart-tooltip-portal');
      if (!portal || portal.style.display === 'none' || portal.style.visibility === 'hidden') return;

      const content = portal.querySelector('.echart-tooltip-content');
      if (!content) return;

      const isScrollable = content.scrollHeight > content.clientHeight;
      if (!isScrollable) return;

      // Tooltip is visible and has overflow — hijack the wheel to scroll it
      e.stopPropagation();
      e.preventDefault();
      content.scrollTop += e.deltaY;
    }, []);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      el.addEventListener('wheel', wheelHandler, { passive: false, capture: true });
      return () => el.removeEventListener('wheel', wheelHandler, { capture: true });
    }, [wheelHandler]);

    return (
      <div
        ref={containerRef}
        className="bg-white rounded-3 overflow-hidden"
        style={{
          position: 'relative', // Ensures positioned elements inside the chart are positioned within this container
          height: height,
          width: width,
          ...customStyles,
        }}
      >
        {controlsLeft && (
          <div
            style={{
              position: 'absolute',
              top: 2,
              left: 2,
              zIndex: 2,
            }}
          >
            {controlsLeft}
          </div>
        )}

        {controlsRight && (
          <div
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              zIndex: 2,
            }}
          >
            {controlsRight}
          </div>
        )}
        <ReactEChartsCore
          ref={ref}
          option={option}
          onEvents={onEvents}
          opts={{ renderer: eChartsRenderer }} // Sets the renderer type
          echarts={echarts} // Pass in the echarts instance so it knows which components have been registered above for use
          notMerge={notMerge}
          autoResize={autoResize}
          lazyUpdate={lazyUpdate}
          style={{ height: '100%', width: '100%' }}
        />
      </div>
    );
  },
);

export default BaseChart;
